import { useEffect, useMemo, useRef, useState } from 'react';
import { useMaestroStore } from '../store/maestroStore';
import {
  classifySignalPathZone,
  shouldShowSignalPathNudge,
  zoneListHasNativeLocalDevice,
} from '../utils/signalPath';
import {
  hydrateSignalPathNudgeDismissal,
  isSignalPathNudgeSuppressed,
  persistSignalPathNudgeDismissal,
} from '../utils/signalPathNudgePrefs';
import {
  getSystemAudioPlaybackMs,
  recordSignalPathPlaybackTick,
} from '../utils/signalPathSession';
import { logSignalPathNudge } from '../utils/signalPathTelemetry';
import { msSinceLastPlayTrackCompleted } from '../transport/QueueController';

export type SignalPathNudgeHandlers = {
  visible: boolean;
  onShowMe: () => void;
  onNotNow: () => void;
};

export function useSignalPathNudge(
  openZonePicker: () => void,
): SignalPathNudgeHandlers {
  const activeZone = useMaestroStore((s) => s.discovery?.activeZone);
  const playbackState = useMaestroStore((s) => s.playbackState);
  const discoveredZones = useMaestroStore((s) => s.discovery?.zones) ?? [];

  const [suppressed, setSuppressed] = useState(() => isSignalPathNudgeSuppressed());
  const [playbackMs, setPlaybackMs] = useState(() => getSystemAudioPlaybackMs());
  const shownRef = useRef(false);

  const zoneType = classifySignalPathZone(activeZone);
  const isSystemAudio = zoneType === 'system_audio';
  const isPlaying = playbackState === 'playing';
  const isTransitioning = playbackState === 'loading' || playbackState === 'buffering';
  const hasNativeDevice = zoneListHasNativeLocalDevice(discoveredZones);

  useEffect(() => {
    void hydrateSignalPathNudgeDismissal().then(() => {
      setSuppressed(isSignalPathNudgeSuppressed());
    });
  }, []);

  useEffect(() => {
    if (!isSystemAudio) return undefined;
    const id = window.setInterval(() => {
      recordSignalPathPlaybackTick(isSystemAudio, isPlaying);
      setPlaybackMs(getSystemAudioPlaybackMs());
    }, 5000);
    return () => window.clearInterval(id);
  }, [isSystemAudio, isPlaying]);

  useEffect(() => {
    recordSignalPathPlaybackTick(isSystemAudio, isPlaying);
    setPlaybackMs(getSystemAudioPlaybackMs());
  }, [isSystemAudio, isPlaying]);

  const visible = useMemo(
    () => shouldShowSignalPathNudge({
      zoneType,
      hasNativeDevice,
      playbackMs,
      suppressed: suppressed || shownRef.current,
      isTransitioning,
      msSinceTrackChange: msSinceLastPlayTrackCompleted(),
    }),
    [
      suppressed,
      zoneType,
      hasNativeDevice,
      playbackMs,
      isTransitioning,
      playbackState,
    ],
  );

  useEffect(() => {
    if (!visible || shownRef.current) return;
    shownRef.current = true;
    logSignalPathNudge('shown', 'system_audio');
  }, [visible]);

  const onShowMe = () => {
    logSignalPathNudge('accepted', 'system_audio');
    persistSignalPathNudgeDismissal();
    setSuppressed(true);
    openZonePicker();
  };

  const onNotNow = () => {
    logSignalPathNudge('dismissed', 'system_audio');
    persistSignalPathNudgeDismissal();
    setSuppressed(true);
  };

  return { visible, onShowMe, onNotNow };
}
